/**
 * Team Inbox — pure presentation helpers: delivery ticks, row snippets,
 * WhatsApp-style time labels and date pills (UI Spec §7.1/§7.3).
 *
 * PURE — no React, no Firebase, clock always injected. Locked by the
 * Node test suite (test/teaminboxSnippets.test.cjs).
 */
import type { BubbleKind, InboxMessage } from './types';

/* ───────────────────── template body upgrade ───────────────────── */

/**
 * Local approved-template body map (mirrors functions/lib/waTemplateBodies.js
 * — keep the two in sync). Used to UPGRADE a legacy/echoed bubble whose body
 * is still the raw `[template:NAME]` placeholder into the real text. New sends
 * already persist the rendered body server-side, so this only repairs
 * historical/echoed messages.
 */
const TEMPLATE_BODIES: Record<string, string> = {
  av_lead_ack_contact_v1:
    'Hi {{1}}, thank you for contacting Aayatan Veda. We have received your enquiry and our team will get back to you shortly.',
  av_lead_ack_service_enquiry_v1:
    'Hi {{1}}, thank you for your service enquiry with Aayatan Veda. Our team will reach out to you shortly to understand your requirement.',
  av_lead_ack_e1_v1:
    'Hi {{1}}, thank you for using our free Vastu tool. Your result is on its way — our team will follow up with the next steps.',
  av_lead_ack_e2_v1:
    'Hi {{1}}, thank you for completing the assessment (Ref {{2}}). Our team will review your details and get back to you shortly.',
  av_lead_ack_e3_v1:
    'Hi {{1}}, thank you for your interest in Aayatan Veda. Our team will connect with you shortly to take this forward.',
  av_lead_ack_e4_v1:
    'Hi {{1}}, thank you for sharing your details (Ref {{2}}). Our team will review your floor plan and get back to you shortly.',
};

function fillPlaceholders(body: string, params: string[]): string {
  return String(body || '').replace(/\{\{(\d+)\}\}/g, (m, n) => {
    const v = params[Number(n) - 1];
    return v == null || v === '' ? m : String(v);
  });
}

/**
 * PURE — upgrade a stored `[template:NAME]` placeholder body to the real text.
 * If `stored` is not a placeholder, returns it unchanged. Unknown templates
 * keep the placeholder (the "Template · NAME" tag still renders the name).
 */
export function upgradeTemplateBody(
  stored: string | undefined | null,
  params: string[] = [],
): string {
  const s = String(stored ?? '');
  const m = s.match(/^\[template:([^\]]+)\]$/);
  if (!m) return s;
  const body = TEMPLATE_BODIES[m[1]];
  return body ? fillPlaceholders(body, params) : s;
}

/* ───────────────────── delivery ticks (R-B6) ───────────────────── */

export interface TickView {
  /** Colour-independent glyph — ◷ ✓ ✓✓ ⚠ (a11y rule §11). */
  glyph: string;
  /** CSS modifier: pending | sent | delivered | read | failed | unknown. */
  state: string;
  /** Accessible label. */
  title: string;
}

/**
 * PURE — deliveryState → tick view. The pipeline vocabulary is
 * sent | delivered | read | failed | unknown (lib/waMedia deliveryStateOf);
 * `null` means no status callback yet → pending ◷. Read = blue ✓✓ via the
 * `read` state class (glyph itself stays colour-independent).
 */
export function tickOf(deliveryState: string | null | undefined): TickView {
  const s = String(deliveryState || '').toLowerCase().trim();
  switch (s) {
    case 'sent':
      return { glyph: '✓', state: 'sent', title: 'Sent' };
    case 'delivered':
      return { glyph: '✓✓', state: 'delivered', title: 'Delivered' };
    case 'read':
      return { glyph: '✓✓', state: 'read', title: 'Read' };
    case 'failed':
      return { glyph: '⚠', state: 'failed', title: 'Failed — tap to retry' };
    case '':
      return { glyph: '◷', state: 'pending', title: 'Pending' };
    default:
      return { glyph: '✓', state: 'unknown', title: s };
  }
}

/* ───────────────────── bubble kind detection ───────────────────── */

/**
 * PURE — classify a workspace message into the §7.2 bubble taxonomy from
 * its attachment kind/contentType. Inbound media kinds come from the
 * router (image / audio / voice / video / document / sticker); outbound
 * attachments are image|document only. Tolerant: unknown kinds render as
 * document cards (never an invisible message).
 */
export function bubbleKindOf(m: {
  body?: string;
  attachment?: { kind?: string | null; contentType?: string | null } | null;
  templateName?: string | null;
  kind?: string | null;
}): BubbleKind {
  if (m.kind === 'note') return 'note'; // internal notes (composing UI = Phase 4)
  if (m.kind === 'system') return 'system'; // §7.3 allocation/escalation lines
  if (m.kind === 'reaction') return 'reaction'; // AD-028 fallback standalone bubble
  if (m.templateName) return 'template';
  const att = m.attachment;
  if (!att) return 'text';
  const kind = String(att.kind || '').toLowerCase();
  const ct = String(att.contentType || '').toLowerCase();
  if (kind === 'audio' || kind === 'voice' || ct.startsWith('audio/')) {
    return 'voice';
  }
  if (kind === 'video' || ct.startsWith('video/')) return 'video';
  // sticker BEFORE the contentType image sniff — stickers are image/webp
  if (kind === 'sticker') return 'sticker';
  if (kind === 'image' || ct.startsWith('image/')) return 'image';
  return 'document';
}

/* ───────────────────── row snippet (§7.1) ───────────────────── */

/** First N words of a transcript for the 🎤 snippet ("…पूजा रूम…"). */
export function transcriptPreview(text: string, words = 5): string {
  const parts = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const head = parts.slice(0, words).join(' ');
  return parts.length > words ? `"${head}…"` : `"${head}"`;
}

/**
 * PURE — the S1 row snippet for a last message (§7.1): outbound prefixed
 * with its tick glyph; media as icon + label (📷 Photo · 🎤 + transcript
 * words · 📄 filename · 🎞 Video). Falls back to the plain text body.
 */
export function snippetOf(m: {
  direction: 'in' | 'out';
  body: string;
  deliveryState: string | null;
  kind: BubbleKind;
  attachment: { fileName: string | null } | null;
}): string {
  let core: string;
  switch (m.kind) {
    case 'image':
      core = `📷 Photo${m.body ? ` · ${m.body}` : ''}`;
      break;
    case 'video':
      core = `🎞 Video${m.body ? ` · ${m.body}` : ''}`;
      break;
    case 'voice':
      core = `🎤 Voice${m.body ? ` · ${transcriptPreview(m.body)}` : ''}`;
      break;
    case 'document':
      core = `📄 ${m.attachment?.fileName || 'Document'}`;
      break;
    case 'sticker':
      core = '🩵 Sticker';
      break;
    case 'template':
      core = m.body || 'Template message';
      break;
    default:
      core = m.body || '—';
  }
  if (m.direction === 'out') {
    return `${tickOf(m.deliveryState).glyph} ${core}`;
  }
  return core;
}

/* ───────────────────── time labels (§7.1) ───────────────────── */

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "7:34" (24h-clock-agnostic via locale would be untestable — fixed H:MM). */
export function clockLabel(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours();
  const m = d.getMinutes();
  return `${h}:${m < 10 ? '0' : ''}${m}`;
}

/**
 * PURE — WhatsApp-style row time relativity (§7.1): clock today · weekday
 * ("Mon") within the last week · "04/06" older. Clock injected for tests.
 */
export function rowTimeLabel(ms: number | null, nowMs: number): string {
  if (!ms) return '';
  const today = startOfDay(nowMs);
  const day = startOfDay(ms);
  if (day === today) return clockLabel(ms);
  const daysAgo = Math.round((today - day) / DAY_MS);
  if (daysAgo >= 1 && daysAgo <= 6) return WEEKDAYS[new Date(ms).getDay()];
  const d = new Date(ms);
  const dd = d.getDate();
  const mm = d.getMonth() + 1;
  return `${dd < 10 ? '0' : ''}${dd}/${mm < 10 ? '0' : ''}${mm}`;
}

const MONTHS = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
];

/** PURE — thread date-pill label: TODAY / YESTERDAY / "4 JUNE" (§7.3). */
export function datePillLabel(ms: number, nowMs: number): string {
  const today = startOfDay(nowMs);
  const day = startOfDay(ms);
  if (day === today) return 'TODAY';
  if (today - day === DAY_MS) return 'YESTERDAY';
  const d = new Date(ms);
  const label = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === new Date(nowMs).getFullYear()
    ? label
    : `${label} ${d.getFullYear()}`;
}

/* ───────────────────── day grouping + unread divider ───────────────────── */

/**
 * PURE — group an ascending message list into date-pill day groups.
 * Messages with no timestamp ride in the current (last) group so nothing
 * is ever dropped.
 */
export function groupByDay(
  messages: InboxMessage[],
  nowMs: number,
): Array<{ label: string; dayStartMs: number; messages: InboxMessage[] }> {
  const groups: Array<{
    label: string;
    dayStartMs: number;
    messages: InboxMessage[];
  }> = [];
  for (const m of messages) {
    const at = m.at;
    if (at == null) {
      if (groups.length === 0) {
        groups.push({ label: 'TODAY', dayStartMs: startOfDay(nowMs), messages: [] });
      }
      groups[groups.length - 1].messages.push(m);
      continue;
    }
    const day = startOfDay(at);
    const last = groups[groups.length - 1];
    if (!last || last.dayStartMs !== day) {
      groups.push({ label: datePillLabel(at, nowMs), dayStartMs: day, messages: [m] });
    } else {
      last.messages.push(m);
    }
  }
  return groups;
}

/**
 * PURE — index (into the ascending message list) where the "N unread
 * messages" divider sits: before the LAST `unread` INBOUND messages.
 * 0 unread → -1 (no divider). Defensive when unread exceeds the inbound
 * count (server counter drift): divider sits before the first inbound.
 */
export function unreadDividerIndex(
  messages: Array<{ direction: 'in' | 'out' }>,
  unread: number,
): number {
  if (!(unread > 0)) return -1;
  let remaining = unread;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].direction !== 'in') continue;
    remaining -= 1;
    if (remaining === 0) return i;
  }
  // counter drift — divider before the earliest inbound (if any)
  const first = messages.findIndex((m) => m.direction === 'in');
  return first;
}
